import React from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCw, SquareTerminal, X } from 'lucide-react';
import { profileProblem } from './profileProblem';
import { notFoundProgram } from './terminalProfiles';

/**
 * Slides in over the bottom of a pane whose program ended. The xterm and its
 * scrollback stay; Restart runs the program again in the same xterm.
 * Enter / Esc do the same while the pane is focused (TerminalPane's key
 * filter), which the button hints say.
 */
export default function PaneExitBar({ name, code, error, onRestart, onOpenShell, onClose, canRestart = true }) {
  const { t } = useTranslation();
  const bad = !!error || (code != null && code !== 0);
  let msg;
  if (error && /^not_found:/.test(error) && profileProblem(notFoundProgram(error)))
    msg = t('terminalExt.notRunnableExit', '{{name}} could not start: it is not a command-line program', { name });
  else if (error && /^not_found:/.test(error)) msg = t('terminalExt.notInstalledExit', '{{name}} is not installed on this computer', { name });
  else if (error === 'unsafe_arg')
    msg = t('terminalExt.unsafeArg', '{{name}} was not started: an argument contains a character cmd.exe would reinterpret (" & | < > ^ % ! ( ))', { name });
  else if (error) msg = t('terminalExt.failedToStart', '{{name}} could not start: {{error}}', { name, error });
  else if (code == null) msg = t('terminalExt.exitedNoCode', '{{name}} exited', { name });
  else msg = t('terminalExt.exitedCode', '{{name}} exited (code {{code}})', { name, code });

  return (
    <div className="term-exit-bar" role="status">
      <span className={`term-exit-msg${bad ? ' term-exit-msg--bad' : ''}`}>{msg}</span>
      {canRestart && (
        <button type="button" className="term-exit-btn term-exit-btn--primary" onClick={onRestart}>
          <RotateCw size={12} aria-hidden />
          {t('terminalExt.restart', 'Restart')}
          <kbd aria-hidden>Enter</kbd>
        </button>
      )}
      {onOpenShell && (
        <button type="button" className="term-exit-btn" onClick={onOpenShell}>
          <SquareTerminal size={12} aria-hidden />
          {t('terminalExt.openShellHere', 'Open shell here')}
        </button>
      )}
      <button type="button" className="term-exit-btn" onClick={onClose}>
        <X size={12} aria-hidden />
        {t('terminalExt.closePane', 'Close pane')}
        <kbd aria-hidden>Esc</kbd>
      </button>
    </div>
  );
}
